import React, { useState, useEffect, useCallback, startTransition, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Session, Feature, Component, SessionType, Platform, SessionUser, SessionTypeItem, PlatformItem, TestPlan } from '../../types';
import { apiRequest } from '../../utils/api';
import { toggleComponent, togglePlatform } from '../../utils/sessionFormUtils';
import { useToast, showTempMessage } from '../../utils/messages';
import { Toast } from '../../utils/messages';
import { useAuth } from '../../contexts/AuthContext';
import { useProduct } from '../../contexts/ProductContext';
import { theme } from '../../theme';
import { useMilestones } from '../../utils/milestones';
import AddComponentModal from './AddComponentModal';
import MilestoneSelector from '../shared/MilestoneSelector';
import '../../App.css';

const CreateSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: loggedInUser } = useAuth();
  const { selectedProduct } = useProduct();
  const [features, setFeatures] = useState<Feature[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [sessionTypes, setSessionTypes] = useState<SessionTypeItem[]>([]);
  const [platforms, setPlatforms] = useState<PlatformItem[]>([]);
  const [users, setUsers] = useState<SessionUser[]>([]);
  const [testPlans, setTestPlans] = useState<TestPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddComponentModal, setShowAddComponentModal] = useState(false);
  const [autoSelectDays, setAutoSelectDays] = useState<number>(7);
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const [buggedComponentIds, setBuggedComponentIds] = useState<Set<string>>(new Set());
  const [autoSelectedBuggedIds, setAutoSelectedBuggedIds] = useState<Set<string>>(new Set());
  const [untestedComponentIds, setUntestedComponentIds] = useState<Set<string>>(new Set());
  const [activeFilters, setActiveFilters] = useState<{ includeUntested: boolean; includeBugged: boolean }>({ includeUntested: false, includeBugged: false });
  const isAutoSelectingRef = useRef(false);
  const { message, setMessage, showMessage } = useToast();

  const [formData, setFormData] = useState<Partial<Session>>({
    productId: '',
    user: '',
    sessionType: '' as SessionType,
    milestone: '',
    platforms: [] as Platform[],
    features: [],
    components: [],
    deadline: '',
    referenceUrl: '',
    testPlanUrl: '',
    notes: '',
    duration: undefined
  });
  
  // Fetch milestones based on selected product
  const milestones = useMilestones(formData.productId);
  
  // Handle cancel - navigate back to sessions
  const handleCancel = () => {
    navigate('/sessions');
  };

  // Update formData.productId immediately when page loads or selectedProduct changes
  useEffect(() => {
    if (selectedProduct?.id && formData.productId !== selectedProduct.id) {
      setFormData(prev => ({ ...prev, productId: selectedProduct.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.id]);

  // Load initial data when page loads
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [typesData, platformsData, usersData] = await Promise.all([
          apiRequest<SessionTypeItem[]>('/api/sessiontypes', {}, { useAuth: true }),
          apiRequest<PlatformItem[]>('/api/platforms', {}, { useAuth: true }),
          apiRequest<SessionUser[]>('/api/users/for-sessions', {}, { useAuth: true }),
        ]);
        
        // Filter session types and platforms by selected product
        const filterByProduct = <T extends { productIds?: string[] }>(items: T[]): T[] => {
          if (!selectedProduct) return items;
          return items.filter(item => 
            !item.productIds || item.productIds.length === 0 || item.productIds.includes(selectedProduct.id)
          );
        };
        
        setSessionTypes(filterByProduct(typesData || []));
        setPlatforms(filterByProduct(platformsData || []));
        setUsers(usersData || []);

        // Check for navigation state (e.g., from Test Plan)
        const state = location.state as {
          productId?: string;
          features?: string[];
          components?: string[];
          platforms?: Platform[];
          referenceUrl?: string;
          testPlanUrl?: string;
          milestone?: string;
        } | null;

        // Set defaults or use navigation state
        const updates: Partial<Session> = {};
        if (state?.productId) {
          updates.productId = state.productId;
        }
        if (state?.platforms && state.platforms.length > 0) {
          updates.platforms = state.platforms;
        } else if (platformsData?.length > 0) {
          updates.platforms = [platformsData[0].name as Platform];
        }
        if (state?.features && state.features.length > 0) {
          updates.features = state.features;
        }
        if (state?.components && state.components.length > 0) {
          updates.components = state.components;
        }
        if (state?.referenceUrl) {
          updates.referenceUrl = state.referenceUrl;
        }
        if (state?.testPlanUrl) {
          updates.testPlanUrl = state.testPlanUrl;
        }
        if (state?.milestone) {
          updates.milestone = state.milestone;
        }
        if (typesData?.length > 0) {
          updates.sessionType = typesData[0].name as SessionType;
        }
        // Default to logged-in user if available
        if (loggedInUser) {
          const loggedInUserInList = usersData.find(u => u.id === loggedInUser.id);
          if (loggedInUserInList) {
            updates.user = loggedInUserInList.displayName;
          } else if (usersData?.length > 0) {
            updates.user = usersData[0].displayName;
          }
        } else if (usersData?.length > 0) {
          updates.user = usersData[0].displayName;
        }
        
        // Default deadline to today's date
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        updates.deadline = `${year}-${month}-${day}`;
        if (Object.keys(updates).length > 0) {
          setFormData(prev => ({ ...prev, ...updates }));
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setLoading(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, loggedInUser, selectedProduct]);

  // Fetch features when product changes
  useEffect(() => {
    if (!formData.productId) {
      setFeatures([]);
      setComponents([]);
      return;
    }

    const fetchFeatures = async () => {
      try {
        const data = await apiRequest<Feature[]>(`/api/features?productId=${formData.productId}`, {}, { useAuth: true });
        const fetchedFeatures = data || [];
        
        if (formData.features && formData.features.length > 0) {
          const fetchedFeatureIds = new Set(fetchedFeatures.map(f => f.id));
          const missingFeatureIds = formData.features.filter(id => !fetchedFeatureIds.has(id));
          
          if (missingFeatureIds.length > 0) {
            const missingFeatures = await Promise.all(
              missingFeatureIds.map(async (id: string) => {
                try {
                  return await apiRequest<Feature>(`/api/features/${id}`, {}, { useAuth: true });
                } catch {
                  return null;
                }
              })
            );
            const validMissingFeatures = missingFeatures.filter((f): f is Feature => f !== null);
            setFeatures([...fetchedFeatures, ...validMissingFeatures]);
            return;
          }
        }
        
        setFeatures(fetchedFeatures);
      } catch (error) {
        console.error('Error fetching features:', error);
      }
    };
    fetchFeatures();
  }, [formData.productId, formData.features]);

  // Helper function to filter components by milestone
  const filterComponentsByMilestone = useCallback((componentsToFilter: Component[], selectedMilestone: string | undefined): Component[] => {
    if (!selectedMilestone || !selectedMilestone.trim()) {
      return componentsToFilter;
    }
    
    const milestoneMeta = milestones[selectedMilestone];
    if (!milestoneMeta) {
      return componentsToFilter.filter(c => c.milestone === selectedMilestone);
    }
    
    const milestoneStartDate = new Date(milestoneMeta.startDate);
    
    return componentsToFilter.filter(c => {
      if (c.milestone === selectedMilestone) {
        return true;
      }
      
      const componentMilestoneMeta = milestones[c.milestone];
      if (!componentMilestoneMeta) {
        return false;
      }
      
      const componentMilestoneStart = new Date(componentMilestoneMeta.startDate);
      return componentMilestoneStart <= milestoneStartDate;
    });
  }, [milestones]);

  // Fetch all components when product changes
  useEffect(() => {
    if (!formData.productId) {
      setComponents([]);
      return;
    }

    const fetchAllComponents = async () => {
      try {
        const componentsData = await apiRequest<Component[]>('/api/components', {}, { useAuth: true });
        if (Array.isArray(componentsData)) {
          const productFeatureIds = new Set(features.map(f => f.id));
          const formDataFeatureIds = new Set(formData.features || []);
          const allRelevantFeatureIds = new Set([...Array.from(productFeatureIds), ...Array.from(formDataFeatureIds)]);
          let filtered = componentsData.filter((c: Component) => allRelevantFeatureIds.has(c.featureId));
          
          if (formData.components && formData.components.length > 0) {
            const filteredComponentIds = new Set(filtered.map(c => c.id));
            const missingComponentIds = formData.components.filter(id => !filteredComponentIds.has(id));
            
            if (missingComponentIds.length > 0) {
              const missingComponents = await Promise.all(
                missingComponentIds.map(async (id: string) => {
                  try {
                    return await apiRequest<Component>(`/api/components/${id}`, {}, { useAuth: true });
                  } catch {
                    return null;
                  }
                })
              );
              const validMissingComponents = missingComponents.filter((c): c is Component => c !== null);
              filtered = [...filtered, ...validMissingComponents];
            }
          }
          
          setComponents(filtered);
        }
      } catch (error) {
        console.error('Error fetching components:', error);
      }
    };
    fetchAllComponents();
  }, [formData.productId, features, formData.features, formData.components]);

  // Fetch test plans when product changes
  useEffect(() => {
    if (!formData.productId) {
      setTestPlans([]);
      return;
    }

    const fetchTestPlans = async () => {
      try {
        const data = await apiRequest<TestPlan[]>(`/api/testplans?productId=${formData.productId}&status=active`, {}, { useAuth: true });
        setTestPlans(data || []);
      } catch (error) {
        console.error('Error fetching test plans:', error);
      }
    };
    fetchTestPlans();
  }, [formData.productId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleComponentToggle = (componentId: string) => {
    setFormData(prev => toggleComponent(prev, componentId));
  };

  const handleComponentsSelected = (featureIds: string[], componentIds: string[]) => {
    setFormData(prev => {
      const currentFeatures = prev.features || [];
      const currentComponents = prev.components || [];
      
      const updatedFeatures = [...currentFeatures];
      featureIds.forEach(featureId => {
        if (!updatedFeatures.includes(featureId)) {
          updatedFeatures.push(featureId);
        }
      });
      
      const updatedComponents = [...currentComponents];
      componentIds.forEach(componentId => {
        if (!updatedComponents.includes(componentId)) {
          updatedComponents.push(componentId);
        }
      });
      
      setAutoSelectedBuggedIds(prev => {
        const newSet = new Set(prev);
        componentIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      setUntestedComponentIds(prev => {
        const newSet = new Set(prev);
        componentIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      
      return {
        ...prev,
        features: updatedFeatures,
        components: updatedComponents
      };
    });
  };

  const handleAutoSelect = useCallback(async (includeUntested: boolean, includeBugged: boolean) => {
    if (!formData.productId) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select a product first' });
      return;
    }

    if (!formData.platforms || formData.platforms.length === 0) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select at least one platform first' });
      return;
    }

    if (autoSelectDays < 0) {
      showTempMessage(setMessage, { type: 'error', text: 'Days must be a positive number' });
      return;
    }

    if (isAutoSelectingRef.current) {
      return;
    }
    
    isAutoSelectingRef.current = true;
    setIsAutoSelecting(true);
    
    try {
      const completedSessions = await apiRequest<Session[]>('/api/sessions?status=completed', {}, { useAuth: true });
      const productSessions = completedSessions.filter(s => s.productId === formData.productId);
      
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - autoSelectDays);
      
      const selectedPlatforms = formData.platforms;
      const componentsToSelect = new Set<string>();
      const featuresToSelect = new Set<string>();
      const buggedComponents = new Set<string>();
      const untestedComponents = new Set<string>();
      
      const milestoneFilteredComponents = filterComponentsByMilestone(components, formData.milestone);
      
      for (const component of milestoneFilteredComponents) {
        const componentPlatforms = component.platforms && component.platforms.length > 0 
          ? component.platforms 
          : null;
        
        const platformsUsedWith = new Set<Platform>();
        
        for (const session of productSessions) {
          const hasComponent = session.components?.includes(component.id) || false;
          if (!hasComponent) continue;
          
          const sessionPlatforms = session.platforms || [session.platform];
          
          selectedPlatforms.forEach(platform => {
            if (sessionPlatforms.includes(platform)) {
              platformsUsedWith.add(platform);
            }
          });
        }
        
        let relevantPlatforms: Platform[];
        if (componentPlatforms) {
          relevantPlatforms = selectedPlatforms.filter(platform => 
            componentPlatforms.includes(platform)
          );
        } else {
          relevantPlatforms = selectedPlatforms.filter(platform => 
            platformsUsedWith.has(platform)
          );
        }
        
        if (relevantPlatforms.length === 0) continue;
        
        const platformStatus = new Map<Platform, { tested: boolean; bugged: boolean }>();
        
        relevantPlatforms.forEach(platform => {
          platformStatus.set(platform, { tested: false, bugged: false });
        });
        
        for (const session of productSessions) {
          const hasComponent = session.components?.includes(component.id) || false;
          if (!hasComponent) continue;
          
          const sessionPlatforms = session.platforms || [session.platform];
          
          const componentStatus = session.componentStatuses?.find(s => s.componentId === component.id);
          const testedPlatforms = componentStatus?.testedPlatforms || [];
          const buggedPlatforms = componentStatus?.buggedPlatforms || [];
          const isComponentBugged = componentStatus?.bugged === true;
          
          const linkedBugs = (session.bugUrls || []).filter(bugUrl => 
            typeof bugUrl === 'object' && bugUrl.componentId === component.id
          );
          const hasLinkedBugs = linkedBugs.length > 0;
          
          if (!session.completedAt) continue;
          const completedDate = new Date(session.completedAt);
          const isWithinTimeframe = completedDate >= cutoffDate;
          
          for (const platform of relevantPlatforms) {
            if (!sessionPlatforms.includes(platform)) continue;
            
            const currentStatus = platformStatus.get(platform)!;
            
            if (isWithinTimeframe && testedPlatforms.includes(platform)) {
              currentStatus.tested = true;
            }
            
            let isBuggedOnPlatform = false;
            if (isWithinTimeframe) {
              if (isComponentBugged) {
                isBuggedOnPlatform = true;
              } else if (buggedPlatforms.includes(platform)) {
                isBuggedOnPlatform = true;
              } else if (hasLinkedBugs) {
                for (const bugUrl of linkedBugs) {
                  if (typeof bugUrl === 'object') {
                    if (bugUrl.platforms && bugUrl.platforms.length > 0) {
                      if (bugUrl.platforms.includes(platform)) {
                        isBuggedOnPlatform = true;
                        break;
                      }
                    } else {
                      isBuggedOnPlatform = true;
                      break;
                    }
                  }
                }
              }
            }
            
            if (isBuggedOnPlatform) {
              currentStatus.bugged = true;
            }
          }
        }
        
        let isBugged = false;
        let isUntested = false;
        
        platformStatus.forEach((status) => {
          if (status.bugged) {
            isBugged = true;
          }
          if (!status.tested) {
            isUntested = true;
          }
        });
        
        let needsTesting = false;
        if (includeBugged && includeUntested) {
          needsTesting = isBugged || isUntested;
        } else if (includeBugged && !includeUntested) {
          needsTesting = isBugged;
        } else if (!includeBugged && includeUntested) {
          needsTesting = isUntested && !isBugged;
        }
        
        if (needsTesting) {
          componentsToSelect.add(component.id);
          featuresToSelect.add(component.featureId);
          
          if (isBugged) {
            buggedComponents.add(component.id);
          } else if (isUntested) {
            untestedComponents.add(component.id);
          }
        }
      }
      
      const shouldReplace = activeFilters.includeUntested || activeFilters.includeBugged;
      
      if (componentsToSelect.size > 0 || shouldReplace) {
        startTransition(() => {
          setFormData(prev => {
            const currentFeatures = prev.features || [];
            const currentComponents = prev.components || [];
            
            let updatedFeatures: string[];
            let updatedComponents: string[];
            
            if (shouldReplace) {
              const autoSelectedIds = new Set([
                ...Array.from(autoSelectedBuggedIds),
                ...Array.from(untestedComponentIds)
              ]);
              
              const manuallySelectedComponents = currentComponents.filter(id => !autoSelectedIds.has(id));
              
              updatedComponents = [...manuallySelectedComponents];
              componentsToSelect.forEach(componentId => {
                if (!updatedComponents.includes(componentId)) {
                  updatedComponents.push(componentId);
                }
              });
              
              const remainingFeatureIds = new Set<string>();
              updatedComponents.forEach(compId => {
                const component = components.find(c => c.id === compId);
                if (component) {
                  remainingFeatureIds.add(component.featureId);
                }
              });
              updatedFeatures = currentFeatures.filter(id => remainingFeatureIds.has(id));
              
              featuresToSelect.forEach(featureId => {
                if (!updatedFeatures.includes(featureId)) {
                  updatedFeatures.push(featureId);
                }
              });
            } else {
              updatedFeatures = [...currentFeatures];
              featuresToSelect.forEach(featureId => {
                if (!updatedFeatures.includes(featureId)) {
                  updatedFeatures.push(featureId);
                }
              });
              
              updatedComponents = [...currentComponents];
              componentsToSelect.forEach(componentId => {
                if (!updatedComponents.includes(componentId)) {
                  updatedComponents.push(componentId);
                }
              });
            }
            
            return {
              ...prev,
              features: updatedFeatures,
              components: updatedComponents
            };
          });
          
          setBuggedComponentIds(prev => {
            if (shouldReplace) {
              const newSet = new Set(prev);
              autoSelectedBuggedIds.forEach(id => newSet.delete(id));
              buggedComponents.forEach(id => newSet.add(id));
              return newSet;
            } else {
              const merged = new Set(prev);
              buggedComponents.forEach(id => merged.add(id));
              return merged;
            }
          });
          
          if (includeBugged) {
            setAutoSelectedBuggedIds(new Set(buggedComponents));
          } else {
            setAutoSelectedBuggedIds(new Set());
          }
          
          if (includeUntested) {
            setUntestedComponentIds(new Set(untestedComponents));
          } else {
            setUntestedComponentIds(new Set());
          }
          
          setActiveFilters({ includeUntested, includeBugged });
        });
        
        const buggedCount = buggedComponents.size;
        const untestedCount = untestedComponents.size;
        
        let messageText = `Selected ${componentsToSelect.size} component${componentsToSelect.size !== 1 ? 's' : ''}`;
        const parts: string[] = [];
        if (untestedCount > 0) {
          parts.push(`${untestedCount} untested`);
        }
        if (buggedCount > 0) {
          parts.push(`${buggedCount} recently bugged`);
        }
        if (parts.length > 0) {
          messageText += `: ${parts.join(', ')}`;
        }
        messageText += ` (last ${autoSelectDays} day${autoSelectDays !== 1 ? 's' : ''})`;
        
        showTempMessage(setMessage, { 
          type: 'success', 
          text: messageText
        });
      }
    } catch (error) {
      console.error('Error auto-selecting components:', error);
      showTempMessage(setMessage, { type: 'error', text: 'Failed to auto-select components. Please try again.' });
    } finally {
      isAutoSelectingRef.current = false;
      setTimeout(() => {
        setIsAutoSelecting(false);
      }, 0);
    }
  }, [formData.productId, formData.platforms, formData.milestone, autoSelectDays, components, setMessage, activeFilters.includeUntested, activeFilters.includeBugged, autoSelectedBuggedIds, untestedComponentIds, filterComponentsByMilestone]);

  const handlePlatformToggle = (platform: Platform) => {
    setFormData(prev => togglePlatform(prev, platform));
  };

  const handleFilterToggle = useCallback(async (filterType: 'all' | 'bugged' | 'untested') => {
    if (!formData.productId || !formData.platforms || formData.platforms.length === 0) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select a product and at least one platform first' });
      return;
    }

    let includeUntested = activeFilters.includeUntested;
    let includeBugged = activeFilters.includeBugged;

    if (filterType === 'all') {
      if (activeFilters.includeUntested && activeFilters.includeBugged) {
        includeUntested = false;
        includeBugged = false;
      } else {
        includeUntested = true;
        includeBugged = true;
      }
    } else if (filterType === 'bugged') {
      includeBugged = !activeFilters.includeBugged;
      includeUntested = activeFilters.includeUntested;
    } else if (filterType === 'untested') {
      includeUntested = !activeFilters.includeUntested;
      includeBugged = activeFilters.includeBugged;
    }

    if (!includeUntested && !includeBugged) {
      const currentAutoSelectedBugged = new Set(autoSelectedBuggedIds);
      const currentUntested = new Set(untestedComponentIds);
      
      setFormData(prev => {
        const currentComponents = prev.components || [];
        const currentFeatures = prev.features || [];
        
        const componentsToRemove = new Set([
          ...Array.from(currentAutoSelectedBugged),
          ...Array.from(currentUntested)
        ]);
        
        const updatedComponents = currentComponents.filter(id => !componentsToRemove.has(id));
        
        const remainingFeatureIds = new Set<string>();
        updatedComponents.forEach(compId => {
          const component = components.find(c => c.id === compId);
          if (component) {
            remainingFeatureIds.add(component.featureId);
          }
        });
        const updatedFeatures = currentFeatures.filter(id => remainingFeatureIds.has(id));
        
        return {
          ...prev,
          components: updatedComponents,
          features: updatedFeatures
        };
      });
      
      setAutoSelectedBuggedIds(new Set());
      setUntestedComponentIds(new Set());
      setBuggedComponentIds(prev => {
        const newSet = new Set(prev);
        currentAutoSelectedBugged.forEach(id => newSet.delete(id));
        return newSet;
      });
      setActiveFilters({ includeUntested: false, includeBugged: false });
    } else {
      await handleAutoSelect(includeUntested, includeBugged);
    }
  }, [formData.productId, formData.platforms, activeFilters, autoSelectedBuggedIds, untestedComponentIds, components, handleAutoSelect, setMessage]);

  const prevPlatformsRef = useRef<string>('');
  useEffect(() => {
    const currentPlatforms = formData.platforms?.sort().join(',') || '';
    if (formData.productId && formData.platforms && formData.platforms.length > 0 && 
        (activeFilters.includeUntested || activeFilters.includeBugged) && 
        !isAutoSelecting &&
        currentPlatforms !== prevPlatformsRef.current) {
      prevPlatformsRef.current = currentPlatforms;
      handleAutoSelect(activeFilters.includeUntested, activeFilters.includeBugged);
    } else if (currentPlatforms !== prevPlatformsRef.current) {
      prevPlatformsRef.current = currentPlatforms;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.platforms, activeFilters.includeUntested, activeFilters.includeBugged, isAutoSelecting]);

  const handleClearAll = () => {
    setFormData(prev => ({
      ...prev,
      features: [],
      components: []
    }));
    setBuggedComponentIds(new Set());
    setAutoSelectedBuggedIds(new Set());
    setUntestedComponentIds(new Set());
    showTempMessage(setMessage, { type: 'success', text: 'All features and components cleared' });
  };

  const validateForm = (): boolean => {
    if (!formData.productId) {
      if (!selectedProduct?.id) {
        showTempMessage(setMessage, { type: 'error', text: 'Please select a product from the navigation bar' });
      } else {
        showTempMessage(setMessage, { type: 'error', text: 'Please select a product' });
      }
      return false;
    }
    if (!formData.user) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select a user' });
      return false;
    }
    if (!formData.features || formData.features.length === 0) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select at least one feature' });
      return false;
    }
    if (!formData.platforms || formData.platforms.length === 0) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select at least one platform' });
      return false;
    }
    if (!formData.deadline) {
      showTempMessage(setMessage, { type: 'error', text: 'Please set a deadline' });
      return false;
    }
    if (!formData.milestone || !formData.milestone.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Please enter a milestone' });
      return false;
    }
    return true;
  };

  const handleSaveForLater = async () => {
    if (!validateForm()) return;

    try {
      await apiRequest<Session>('/api/sessions', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          status: 'todo'
        })
      }, { useAuth: true, setMessage });
      
      showTempMessage(setMessage, { type: 'success', text: 'Session saved successfully' }, 3000, true);
      navigate('/sessions');
    } catch (error) {
      console.error('Error saving session:', error);
    }
  };

  const handleStartSession = async () => {
    if (!validateForm()) return;

    try {
      const startTime = new Date().toISOString();
      
      const session = await apiRequest<Session>('/api/sessions', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          status: 'inprogress',
          startedAt: startTime,
          firstStartedAt: startTime,
          componentStatuses: (formData.components || []).map(componentId => ({
            componentId,
            tested: false,
            bugged: false
          })),
          bugUrls: []
        })
      }, { useAuth: true, setMessage });
      
      showTempMessage(setMessage, { type: 'success', text: 'Session started successfully' }, 3000, true);
      navigate(`/session/${session.id}`);
    } catch (error) {
      console.error('Error starting session:', error);
    }
  };

  const hasProduct = !!formData.productId;
  const hasPlatforms = formData.platforms && formData.platforms.length > 0;

  return (
    <div className="form-container">
      <Toast message={message} />
      
      <div className="page-header">
        <h1 className="page-title">Create New Session</h1>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4rem',
          color: theme.textSecondary,
          fontSize: '1rem'
        }}>
          Loading...
        </div>
      )}

      {!loading && (
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Section 1: Session Basics */}
          <div style={{
            marginBottom: '2rem',
            padding: '1.5rem',
            backgroundColor: 'var(--surface-elevated)',
            borderRadius: '8px',
            border: '1px solid var(--grid-line)'
          }}>
            <h2 style={{
              margin: '0 0 1.5rem 0',
              fontSize: '1.125rem',
              fontWeight: '600',
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ 
                width: '4px', 
                height: '20px', 
                backgroundColor: 'var(--cyan-glow)',
                borderRadius: '2px'
              }}></span>
              Session Basics
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="productId" style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                  Product <span style={{ color: 'var(--danger)', fontWeight: '700', marginLeft: '0.25rem' }}>*</span>
                </label>
                <input
                  type="text"
                  value={selectedProduct?.name || 'No product selected'}
                  disabled
                  style={{
                    backgroundColor: 'var(--surface-secondary)',
                    color: 'var(--text-secondary)',
                    cursor: 'not-allowed',
                    padding: '0.75rem',
                    border: '1px solid var(--grid-line)',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="user" style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                  User <span style={{ color: 'var(--danger)', fontWeight: '700', marginLeft: '0.25rem' }}>*</span>
                </label>
                <select
                  id="user"
                  name="user"
                  value={formData.user}
                  onChange={handleInputChange}
                  required
                  style={{
                    borderLeft: '3px solid var(--primary)',
                    paddingLeft: 'calc(0.75rem - 2px)',
                    paddingRight: '2.5rem',
                    background: 'linear-gradient(to right, rgba(2, 132, 199, 0.03) 0%, transparent 3px), url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' fill=\'%2300d4ff\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M8 11L3 6h10l-5 5z\'/%3E%3C/svg%3E") no-repeat right 1rem center',
                    appearance: 'none'
                  }}
                >
                  <option value="">Select a user</option>
                  {users.map(user => (
                    <option key={user.id} value={user.displayName}>{user.displayName}</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="sessionType" style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                  Type of Session <span style={{ color: 'var(--danger)', fontWeight: '700', marginLeft: '0.25rem' }}>*</span>
                </label>
                <select
                  id="sessionType"
                  name="sessionType"
                  value={formData.sessionType}
                  onChange={handleInputChange}
                  required
                  style={{
                    borderLeft: '3px solid var(--primary)',
                    paddingLeft: 'calc(0.75rem - 2px)',
                    paddingRight: '2.5rem',
                    background: 'linear-gradient(to right, rgba(2, 132, 199, 0.03) 0%, transparent 3px), url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' fill=\'%2300d4ff\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M8 11L3 6h10l-5 5z\'/%3E%3C/svg%3E") no-repeat right 1rem center',
                    appearance: 'none'
                  }}
                >
                  {sessionTypes.map(type => (
                    <option key={type.id} value={type.name}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="milestone" style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                  Milestone <span style={{ color: 'var(--danger)', fontWeight: '700', marginLeft: '0.25rem' }}>*</span>
                </label>
                <div style={{
                  borderLeft: '3px solid var(--primary)',
                  paddingLeft: 'calc(0.5rem - 2px)',
                  background: 'linear-gradient(to right, rgba(2, 132, 199, 0.03) 0%, transparent 3px)',
                  borderRadius: '4px'
                }}>
                  <MilestoneSelector
                    key={formData.productId || selectedProduct?.id || 'no-product'}
                    value={formData.milestone || ''}
                    onChange={(milestone) => {
                      setFormData(prev => ({ ...prev, milestone }));
                    }}
                    productId={formData.productId || selectedProduct?.id}
                    placeholder="e.g., v2.1.0"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Testing Plan */}
          <div style={{
            marginBottom: '2rem',
            padding: '1.5rem',
            backgroundColor: 'var(--surface-elevated)',
            borderRadius: '8px',
            border: `1px solid ${hasProduct ? 'var(--grid-line)' : 'rgba(211, 47, 47, 0.3)'}`,
            opacity: hasProduct ? 1 : 0.6,
            transition: 'all 0.3s ease'
          }}>
            <h2 style={{
              margin: '0 0 1.5rem 0',
              fontSize: '1.125rem',
              fontWeight: '600',
              color: hasProduct ? 'var(--text-primary)' : 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ 
                width: '4px', 
                height: '20px', 
                backgroundColor: hasProduct ? 'var(--cyan-glow)' : 'var(--text-muted)',
                borderRadius: '2px'
              }}></span>
              Testing Plan
              {!hasProduct && (
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: '400',
                  color: 'var(--text-muted)',
                  fontStyle: 'italic',
                  marginLeft: '0.5rem'
                }}>
                  (Select a product first)
                </span>
              )}
            </h2>

            {/* Test Plan (Optional) */}
            {formData.productId && (
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label>Link Test Plan (Optional)</label>
                <select
                  value={formData.testPlanUrl ? testPlans.find(tp => `${window.location.origin}/testplan/${tp.id}` === formData.testPlanUrl)?.id || '' : ''}
                  style={{
                    width: '100%',
                    padding: '0.75rem 2.5rem 0.75rem 1rem',
                    appearance: 'none',
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2300d4ff' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E\")",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center',
                  }}
                  onChange={async (e) => {
                    const selectedTestPlanId = e.target.value;
                    if (selectedTestPlanId) {
                      try {
                        const testPlan = await apiRequest<TestPlan>(`/api/testplans/${selectedTestPlanId}`, {}, { useAuth: true });
                        const testPlanUrl = `${window.location.origin}/testplan/${selectedTestPlanId}`;
                        
                        const testPlanComponents = testPlan.components || [];
                        const testPlanPlatforms = (testPlan.testingPlan || []) as Platform[];
                        
                        const allComponents = await apiRequest<Component[]>('/api/components', {}, { useAuth: true });
                        
                        const componentFeatures = new Set<string>();
                        testPlanComponents.forEach(componentId => {
                          const component = allComponents.find(c => c.id === componentId);
                          if (component) {
                            componentFeatures.add(component.featureId);
                          }
                        });
                        
                        setFormData(prev => ({
                          ...prev,
                          testPlanUrl,
                          components: testPlanComponents,
                          platforms: testPlanPlatforms,
                          features: Array.from(componentFeatures)
                        }));
                        
                        setAutoSelectedBuggedIds(new Set());
                        setUntestedComponentIds(new Set());
                        
                        showTempMessage(setMessage, { type: 'success', text: `Test plan loaded: ${testPlanComponents.length} component(s) and ${testPlanPlatforms.length} platform(s) selected` });
                      } catch (error) {
                        console.error('Error fetching test plan details:', error);
                        showTempMessage(setMessage, { type: 'error', text: 'Failed to load test plan details' });
                      }
                    } else {
                      setFormData(prev => ({ ...prev, testPlanUrl: '' }));
                    }
                  }}
                  className="form-control"
                  disabled={!formData.productId}
                >
                  <option value="">-- None --</option>
                  {testPlans.map(testPlan => (
                    <option key={testPlan.id} value={testPlan.id}>
                      {testPlan.id} - {testPlan.title}
                    </option>
                  ))}
                </select>
                {formData.testPlanUrl && (
                  <p style={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: '0.875rem', 
                    marginTop: '0.5rem',
                    fontStyle: 'italic'
                  }}>
                    Test Plan linked: {testPlans.find(tp => `${window.location.origin}/testplan/${tp.id}` === formData.testPlanUrl)?.title || 'Unknown'}
                  </p>
                )}
              </div>
            )}

            {/* Platforms */}
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label>Platforms * (Multi-select)</label>
              <div className="multi-select">
                {platforms.map(platform => (
                  <div key={platform.id} className="multi-select-item">
                    <input
                      type="checkbox"
                      id={`platform-${platform.id}`}
                      checked={formData.platforms?.includes(platform.name as Platform) || false}
                      onChange={() => handlePlatformToggle(platform.name as Platform)}
                      disabled={!hasProduct}
                    />
                    <label htmlFor={`platform-${platform.id}`}>{platform.name}</label>
                  </div>
                ))}
              </div>
              {(!formData.platforms || formData.platforms.length === 0) && (
                <p style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Please select at least one platform
                </p>
              )}
            </div>

            {/* Features & Components */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ marginBottom: '1rem', display: 'block' }}>Features & Components <span className="text-danger">*</span></label>
              {hasProduct ? (
                <>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setShowAddComponentModal(true)}
                      style={{ minWidth: '200px', marginBottom: '1rem' }}
                    >
                      Add Features & Components
                    </button>
                    
                    {/* Auto-select Section */}
                    <div style={{ 
                      padding: '1rem',
                      backgroundColor: 'var(--surface-elevated)',
                      borderRadius: '6px',
                      border: `1px solid ${theme.gridLine}`
                    }}>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: theme.textPrimary, 
                        fontWeight: '500', 
                        marginBottom: '0.75rem'
                      }}>
                        Auto-select Components
                      </div>
                      
                      <div style={{ 
                        display: 'flex', 
                        gap: '0.75rem', 
                        alignItems: 'center', 
                        flexWrap: 'wrap'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.5rem', 
                          alignItems: 'center',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{ fontSize: '0.875rem', color: theme.textSecondary }}>Last</span>
                          <input
                            type="number"
                            min="0"
                            value={autoSelectDays}
                            onChange={(e) => {
                              const newDays = Math.max(0, parseInt(e.target.value) || 0);
                              setAutoSelectDays(newDays);
                              if (activeFilters.includeUntested || activeFilters.includeBugged) {
                                handleAutoSelect(activeFilters.includeUntested, activeFilters.includeBugged);
                              }
                            }}
                            style={{
                              width: '50px',
                              padding: '0.375rem',
                              border: `1px solid ${theme.gridLine}`,
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              backgroundColor: theme.surface,
                              color: theme.textPrimary,
                              textAlign: 'center'
                            }}
                          />
                          <span style={{ fontSize: '0.875rem', color: theme.textSecondary }}>days</span>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => handleFilterToggle('all')}
                            disabled={isAutoSelecting || !hasPlatforms}
                            style={{ 
                              padding: '0.5rem 1rem',
                              fontSize: '0.875rem',
                              opacity: (!hasPlatforms || isAutoSelecting) ? 0.5 : 1,
                              cursor: (!hasPlatforms || isAutoSelecting) ? 'not-allowed' : 'pointer',
                              backgroundColor: (activeFilters.includeUntested && activeFilters.includeBugged) ? theme.cyanGlow : undefined,
                              color: (activeFilters.includeUntested && activeFilters.includeBugged) ? theme.surface : undefined,
                              border: (activeFilters.includeUntested && activeFilters.includeBugged) ? `1px solid ${theme.cyanGlow}` : undefined
                            }}
                            title={!hasPlatforms ? "Select platforms first" : "Toggle all untested and recently bugged components"}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => handleFilterToggle('bugged')}
                            disabled={isAutoSelecting || !hasPlatforms}
                            style={{ 
                              padding: '0.5rem 1rem',
                              fontSize: '0.875rem',
                              opacity: (!hasPlatforms || isAutoSelecting) ? 0.5 : 1,
                              cursor: (!hasPlatforms || isAutoSelecting) ? 'not-allowed' : 'pointer',
                              backgroundColor: activeFilters.includeBugged ? theme.danger : undefined,
                              color: activeFilters.includeBugged ? theme.surface : undefined,
                              border: activeFilters.includeBugged ? `1px solid ${theme.danger}` : undefined
                            }}
                            title={!hasPlatforms ? "Select platforms first" : "Toggle recently bugged components"}
                          >
                            Bugged
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => handleFilterToggle('untested')}
                            disabled={isAutoSelecting || !hasPlatforms}
                            style={{ 
                              padding: '0.5rem 1rem',
                              fontSize: '0.875rem',
                              opacity: (!hasPlatforms || isAutoSelecting) ? 0.5 : 1,
                              cursor: (!hasPlatforms || isAutoSelecting) ? 'not-allowed' : 'pointer',
                              backgroundColor: activeFilters.includeUntested ? theme.cyanGlow : undefined,
                              color: activeFilters.includeUntested ? theme.surface : undefined,
                              border: activeFilters.includeUntested ? `1px solid ${theme.cyanGlow}` : undefined
                            }}
                            title={!hasPlatforms ? "Select platforms first" : "Toggle untested components"}
                          >
                            Untested
                          </button>
                        </div>
                      </div>
                      
                      {!hasPlatforms && (
                        <p style={{ 
                          fontSize: '0.75rem', 
                          color: theme.textMuted, 
                          marginTop: '0.75rem',
                          fontStyle: 'italic'
                        }}>
                          Select platforms above to enable auto-select
                        </p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <p style={{ 
                  color: 'var(--text-muted)', 
                  fontSize: '0.875rem', 
                  fontStyle: 'italic',
                  padding: '1rem',
                  backgroundColor: 'var(--void)',
                  borderRadius: '4px',
                  border: '1px dashed var(--grid-line)'
                }}>
                  Select a product above to add features and components
                </p>
              )}
              {(formData.features && formData.features.length > 0) || (formData.components && formData.components.length > 0) ? (
                <div style={{ 
                  marginTop: '1.5rem',
                  padding: '1.25rem', 
                  backgroundColor: theme.surfaceElevated, 
                  borderRadius: '6px',
                  border: `1px solid ${theme.gridLine}`
                }}>
                  {/* Features */}
                  {formData.features && formData.features.length > 0 && (
                    <div style={{ marginBottom: formData.components && formData.components.length > 0 ? '1.25rem' : '0' }}>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: theme.textSecondary, 
                        marginBottom: '0.75rem', 
                        fontWeight: '500' 
                      }}>
                        Selected Features ({formData.features.length})
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {formData.features.map(featureId => {
                          const feature = features.find(f => f.id === featureId);
                          if (!feature) return null;
                          return (
                            <div
                              key={featureId}
                              style={{
                                padding: '0.5rem 0.875rem',
                                backgroundColor: theme.cyanTransparent,
                                color: theme.cyanGlow,
                                border: `1px solid ${theme.cyanGlow}`,
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                            >
                              <span>{feature.name}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => {
                                    const updatedFeatures = (prev.features || []).filter(id => id !== featureId);
                                    const featureComponents = components.filter(c => c.featureId === featureId).map(c => c.id);
                                    const updatedComponents = (prev.components || []).filter(id => !featureComponents.includes(id));
                                    
                                    setBuggedComponentIds(prev => {
                                      const newSet = new Set(prev);
                                      featureComponents.forEach(compId => newSet.delete(compId));
                                      return newSet;
                                    });
                                    setAutoSelectedBuggedIds(prev => {
                                      const newSet = new Set(prev);
                                      featureComponents.forEach(compId => newSet.delete(compId));
                                      return newSet;
                                    });
                                    setUntestedComponentIds(prev => {
                                      const newSet = new Set(prev);
                                      featureComponents.forEach(compId => newSet.delete(compId));
                                      return newSet;
                                    });
                                    
                                    return {
                                      ...prev,
                                      features: updatedFeatures,
                                      components: updatedComponents
                                    };
                                  });
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: theme.cyanGlow,
                                  cursor: 'pointer',
                                  fontSize: '1.1rem',
                                  padding: 0,
                                  width: '18px',
                                  height: '18px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  lineHeight: 1
                                }}
                                title="Remove feature"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Components */}
                  {formData.components && formData.components.length > 0 && (
                    <div>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: theme.textSecondary, 
                        marginBottom: '0.75rem', 
                        fontWeight: '500' 
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.75rem', 
                          flexWrap: 'wrap', 
                          marginBottom: '0.75rem' 
                        }}>
                          <span>Selected Components ({formData.components.length})</span>
                          {autoSelectedBuggedIds.size > 0 && (
                            <span style={{ 
                              fontSize: '0.75rem', 
                              color: theme.danger,
                              fontWeight: '500',
                              padding: '0.25rem 0.625rem',
                              backgroundColor: theme.dangerTransparent || 'rgba(239, 68, 68, 0.1)',
                              borderRadius: '4px',
                              border: `1px solid ${theme.danger}`
                            }}>
                              {autoSelectedBuggedIds.size} bugged
                            </span>
                          )}
                          {untestedComponentIds.size > 0 && (
                            <span style={{ 
                              fontSize: '0.75rem', 
                              color: theme.cyanGlow,
                              fontWeight: '500',
                              padding: '0.25rem 0.625rem',
                              backgroundColor: theme.cyanTransparent,
                              borderRadius: '4px',
                              border: `1px solid ${theme.cyanGlow}`
                            }}>
                              {untestedComponentIds.size} untested
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {formData.components.map(componentId => {
                          const component = components.find(c => c.id === componentId);
                          if (!component) return null;
                          const isBugged = buggedComponentIds.has(componentId);
                          const wasAutoSelectedAsBugged = autoSelectedBuggedIds.has(componentId);
                          const wasAutoSelectedAsUntested = untestedComponentIds.has(componentId);
                          return (
                            <div
                              key={componentId}
                              style={{
                                padding: '0.5rem 0.875rem',
                                backgroundColor: wasAutoSelectedAsBugged ? (theme.dangerTransparent || 'rgba(239, 68, 68, 0.1)') : theme.cyanTransparent,
                                color: wasAutoSelectedAsBugged ? theme.danger : theme.cyanGlow,
                                border: `1px solid ${wasAutoSelectedAsBugged ? theme.danger : theme.cyanGlow}`,
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                              title={wasAutoSelectedAsBugged ? 'Recently bugged - needs retesting' : wasAutoSelectedAsUntested ? 'Untested component' : 'Component'}
                            >
                              <span>{component.name}</span>
                              {wasAutoSelectedAsBugged && (
                                <span style={{ 
                                  fontSize: '0.7rem', 
                                  opacity: 0.9,
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  bugged
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  handleComponentToggle(componentId);
                                  setBuggedComponentIds(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(componentId);
                                    return newSet;
                                  });
                                  setAutoSelectedBuggedIds(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(componentId);
                                    return newSet;
                                  });
                                  setUntestedComponentIds(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(componentId);
                                    return newSet;
                                  });
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: isBugged ? theme.danger : theme.cyanGlow,
                                  cursor: 'pointer',
                                  fontSize: '1.1rem',
                                  padding: 0,
                                  width: '18px',
                                  height: '18px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  lineHeight: 1
                                }}
                                title="Remove component"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div style={{ 
                    marginTop: '1.5rem', 
                    paddingTop: '1rem', 
                    borderTop: `1px solid ${theme.gridLine}` 
                  }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleClearAll}
                      title="Clear all selected features and components"
                      style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Section 3: Additional Details */}
          <div style={{
            marginBottom: '2rem',
            padding: '1.5rem',
            backgroundColor: 'var(--surface-elevated)',
            borderRadius: '8px',
            border: '1px solid var(--grid-line)'
          }}>
            <h2 style={{
              margin: '0 0 1.5rem 0',
              fontSize: '1.125rem',
              fontWeight: '600',
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ 
                width: '4px', 
                height: '20px', 
                backgroundColor: 'var(--cyan-glow)',
                borderRadius: '2px'
              }}></span>
              Additional Details
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="deadline" style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                  Deadline <span style={{ color: 'var(--danger)', fontWeight: '700', marginLeft: '0.25rem' }}>*</span>
                </label>
                <input
                  type="date"
                  id="deadline"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleInputChange}
                  required
                  style={{
                    borderLeft: '3px solid var(--primary)',
                    paddingLeft: 'calc(0.75rem - 2px)',
                    background: 'linear-gradient(to right, rgba(2, 132, 199, 0.03) 0%, transparent 3px)'
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="referenceUrl">Reference URL / Context</label>
                <input
                  type="url"
                  id="referenceUrl"
                  name="referenceUrl"
                  value={formData.referenceUrl}
                  onChange={handleInputChange}
                  placeholder="e.g., PR link, RC link, etc."
                />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: '1.5rem', marginBottom: 0 }}>
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes or context for this session"
              />
            </div>
          </div>

          <div className="button-group" style={{ 
            display: 'flex', 
            gap: '1rem', 
            justifyContent: 'flex-end',
            paddingTop: '1rem',
            paddingBottom: '2rem',
            borderTop: `1px solid ${theme.gridLine}`
          }}>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button className="btn btn-secondary" onClick={handleSaveForLater}>
              Save for Later
            </button>
            <button className="btn btn-primary" onClick={handleStartSession}>
              Start Session
            </button>
          </div>
        </div>
      )}

      {/* Add Component Modal */}
      {formData.productId && (
        <AddComponentModal
          allFeatures={features}
          allComponents={components}
          allPlatforms={platforms}
          isOpen={showAddComponentModal}
          onClose={() => setShowAddComponentModal(false)}
          onComponentsAdded={handleComponentsSelected}
          showMessage={showMessage}
          alreadySelectedComponentIds={formData.components || []}
          milestone={formData.milestone}
          milestones={milestones}
        />
      )}
    </div>
  );
};

export default CreateSessionPage;
